/* eslint-disable no-nested-ternary */

import React, { ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import BooleanInput from '../../input/BooleanInput';

import { FormInputsType } from '../../input/FormType';
import CheckboxInput from '../../input/CheckboxInput';
import RadioPriceCard from '../../input/RadioPriceCard';
import NumberInput from '../../input/NumberInput';
import { apiRoute as api, DirectusFile, getUrlFromSlug, fetchJson } from '../../helpers/ApiHelper';

type CampPrestationsType = {
  // Step 2
  mtbs: boolean;
  discoMat: boolean;
  cinemaMat: boolean;
  sportsMat: boolean;
  bikeNb: number;
  bikeDays: number;
  activityPack: 's' | 'm' | 'l' | 'classique' | null;
};

type CampPrestationsFormProps = CampPrestationsType & {
  // eslint-disable-next-line no-unused-vars
  updateFields: (fields: Partial<FormInputsType>) => void;
};

export default function Step2({
  mtbs,
  discoMat,
  cinemaMat,
  sportsMat,
  bikeDays,
  bikeNb,
  activityPack,
  updateFields,
}: CampPrestationsFormProps) {
  const { t } = useTranslation('', { keyPrefix: 'Config' });
  const tPacks = useTranslation('', { keyPrefix: 'Packs' }).t;

  const [customMat, setCustomMat] = useState<boolean>(activityPack === null);

  const switchingToCustomMat = () => {
    window.scrollTo(0, 0);
    setCustomMat(!customMat);

    // Mettre à jour les valeurs des champs
    updateFields({
      activityPack: customMat ? 's' : null,
      mtbs: customMat,
      discoMat: customMat,
      cinemaMat: customMat,
      sportsMat: customMat,
      bikeNb: 0,
      bikeDays: 0,
    });
  };

  const upgradeToPack = () => {
    window.scrollTo(0, 0);
    // Upgrading from "Material Only" to the other packs (unilateral switch)
    updateFields({ introAnimations: true, activityPack: 'l' });
  };

  // Dynamic Files URLs
  const [files, setFiles] = useState<DirectusFile[]>();

  useEffect(() => {
    // Get all files
    fetchJson<{ data: DirectusFile[] }>(`${api}/files`)
      .then((json) => {
        setFiles(json.data);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(err);
      });
  }, []);

  const cardOptions: {
    value: string;
    surtitle?: string;
    title: string;
    subtitle: string;
    price: number;
    priceText?: string;
    body: string | ReactElement;
  }[] = [
    {
      value: 's',
      surtitle: `${tPacks('activities.0.surname')}${mtbs ? '+' : ''}`,
      title: tPacks('activities.0.name'),
      subtitle: t('packs.s.subtitle'),
      priceText: `${t('packs.s.priceText')}${mtbs ? `, ${t('mtbs.withMtb')}` : ''}`,
      price: mtbs ? 15 : 8,
      body: (
        <>
          <b>{t('packs.s.s1title')}</b>
          <p>{t('packs.s.s1desc')}</p>

          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s1slug`), files)?.id}`}
            target="_blank"
          >
            {t('inventory')}
          </Link>

          <hr />
          <b>{t('packs.s.s2title')}</b>
          <p>{t('packs.s.s2desc')}</p>

          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s2slug`), files)?.id}`}
            target="_blank"
          >
            {t('inventory')}
          </Link>
          <hr />
          <b>{t('packs.s.s3title')}</b>
          <p>{t('packs.s.s3desc')}</p>
          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s3slug`), files)?.id}`}
            target="_blank"
          >
            {t('inventory')}
          </Link>
          <hr />
          <span className={`${mtbs || 'notAvailable'}`}>
            <b>{t('packs.s.s4title')}</b>
            <p>{t('packs.s.s4desc')}</p>
            <Link
              to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s4slug`), files)?.id}`}
              target="_blank"
            >
              {t('inventory')}
            </Link>
          </span>
        </>
      ),
    },
  ];

  return (
    <section className="configuratorStep" id="step2">
      {customMat ? (
        <article className="container medTight">
          <div className="titles">
            <h3 className="centeredDashed">{t('step2.customTitle')}</h3>
            <p>{t('step2.customDesc')}</p>
          </div>
          <div className="checkboxes">
            <legend>{t('step2.mat')}</legend>

            <CheckboxInput
              title={t('step2.matDisco')}
              name="discoMat"
              checked={discoMat}
              updateSingleField={updateFields}
            />

            <CheckboxInput
              title={t('step2.matCine')}
              name="cinemaMat"
              checked={cinemaMat}
              updateSingleField={updateFields}
            />

            <CheckboxInput
              title={t('step2.matSport')}
              name="sportsMat"
              checked={sportsMat}
              updateSingleField={updateFields}
            />

            <legend>{t('mtbs.title')}</legend>

            <div className="inline-numbers">
              <NumberInput
                title={t('step2.bikesFor')}
                name="bikeNb"
                value={bikeNb}
                updateSingleField={updateFields}
              />

              <NumberInput
                title={t('step2.days')}
                name="bikeDays"
                value={bikeDays}
                updateSingleField={updateFields}
              />
            </div>
          </div>
        </article>
      ) : (
        <>
          <article className="container medTight">
            <div className="titles">
              <h3 className="centeredDashed">{t('step2.title')}</h3>
              <p>{t('step2.desc')}</p>
            </div>
            <div className="cardsContainer">
              <RadioPriceCard
                name="activityPack"
                title=""
                options={cardOptions}
                selectedOption="s"
                updateSingleField={updateFields}
              />
            </div>
          </article>
          <article className="container tight">
            <h3 className="centeredDashed">{t('mtbs.title')}</h3>
            <p>{t('mtbs.desc')}</p>
            <BooleanInput
              name="mtbs"
              title={t('mtbs.label')}
              initValue={mtbs}
              updateSingleField={updateFields}
            />
            <small>{t('mtbs.small')}</small>
            <Link
              to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s4slug`), files)?.id}`}
              target="_blank"
            >
              {t('mtbs.link')}
            </Link>
          </article>
        </>
      )}

      <hr />

      {customMat ? (
        <div id="changePack" className="cta cta--config">
          <div className="ctaContent">
            <div className="ctaContentTexts">
              <h4>{t('step2.ctaPackSSurtitle')}</h4>
              <h3>{t('step2.ctaPackSTitle')}</h3>
            </div>

            <button className="btn btn--white" type="button" onClick={switchingToCustomMat}>
              {t('step2.ctaPackSBtn')}
            </button>
          </div>
        </div>
      ) : (
        <div id="changePack" className="cta cta--config">
          <div className="ctaContent">
            <div className="ctaContentTexts">
              <h4>{t('step2.ctaCustomSurtitle')}</h4>
              <h3>{t('step2.ctaCustomTitle')}</h3>
            </div>

            <button className="btn btn--white" type="button" onClick={switchingToCustomMat}>
              {t('step2.ctaCustomBtn')}
            </button>
          </div>
        </div>
      )}

      <div id="changePack" className="cta cta--config">
        <div className="ctaContent">
          <div className="ctaContentTexts">
            <h4>{t('step2.ctaUpgradeSurtitle')}</h4>
            <h3>{t('step2.ctaUpgradeTitle')}</h3>
          </div>

          <button className="btn btn--white" type="button" onClick={upgradeToPack}>
            {t('step2.ctaUpgradeBtn')}
          </button>
        </div>
      </div>

      <hr />
    </section>
  );
}
